import { useStore } from '@store/index';

import { useWorkflow } from '@/hooks/useWorkflows';

/**
 * Resolve the agent UUID the disposition engine needs to resolve program flags
 * (see `lib/disposition/agentId.ts` for why it matters).
 *
 * Deliberately keyed on the CASE's `workflow_id` rather than the store's *selected*
 * workflow: this mirrors bvagent's server-side resolution exactly
 * (`get_case_record_with_agent_id()` → GetCaseRecord → `case.workflow_id` → GetWorkflow →
 * `workflow.agent_id`), so a live call, a PCE recompute and the dashboard all resolve the
 * same flags. The selected workflow can differ from the case's on a deep link.
 *
 * Store-first (zero fetch — the workflows map is already populated for the program), with
 * the cached `useWorkflow` query as a fallback for the case where the case's workflow isn't
 * in that map. The fallback is deliberate belt-and-braces: a silent store miss would
 * reproduce the very bug this resolves (flags quietly resolving `false`) with nothing to
 * notice, so we pay one cached request rather than fail open.
 */
export function useEngineAgentId(workflowId: string | null | undefined): string | undefined {
  const fromStore = useStore(state =>
    workflowId ? state.workflows[workflowId]?.agent_id : undefined,
  );
  // Pass '' when the store already answered (or there's no workflow yet) so the query
  // stays disabled — `useWorkflow` gates on `enabled: !!workflowId`.
  const { data } = useWorkflow(!workflowId || fromStore ? '' : workflowId);
  return fromStore ?? data?.workflow?.agent_id;
}
